import { Form, Formik } from "formik";
import { useCallback, useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Button } from "@volca/ui/components/ui/button";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { TypeableDatePicker } from "@volca/ui/components/ui/typeable-date-picker";
import type { IEnrichedBusiness } from "@/business/business";
import { extractErrorMessage } from "@/core/api/errors";
import {
  buildSchedulePayload,
  getInitialBusinessSetupValues,
  needsReferenceDate,
  needsStartDay,
  PAY_PERIOD_CADENCE_LABEL,
  PAY_PERIOD_CADENCE_OPTIONS,
  START_DAY_OF_WEEK_OPTIONS,
  START_DAY_OF_WEEK_LABEL,
  validateScheduleStep,
} from "./business-setup";
import { FieldError } from "./field-error";
import { PublishStepPayload } from "./publish-step-payload";
import { useOnboardingNavigation } from "./shared-query";
import { StepLayout } from "./step-layout";
import { useStepDialog } from "./use-step-dialog";

interface ScheduleStepScreenProps {
  business: Pick<
    IEnrichedBusiness,
    "id" | "name" | "time_zone_label" | "current_pay_period_template"
  >;
}

export const ScheduleStepScreen = ({ business }: ScheduleStepScreenProps) => {
  const initialValues = getInitialBusinessSetupValues(business);
  const [cadenceSearch, setCadenceSearch] = useState(
    initialValues.cadence
      ? PAY_PERIOD_CADENCE_LABEL[initialValues.cadence]
      : "",
  );
  const [startDaySearch, setStartDaySearch] = useState(
    initialValues.startDayOfWeek
      ? START_DAY_OF_WEEK_LABEL[initialValues.startDayOfWeek]
      : "",
  );
  // The calendar is a portal, so it stays on screen when this step goes hidden.
  // Close it with the step. The typed value in the field is retained.
  const [calendarRequested, setCalendarRequested] = useState(false);
  const closeCalendar = useCallback(() => setCalendarRequested(false), []);
  const calendarOpen = useStepDialog(calendarRequested, closeCalendar);
  const saveMutation = useOnboardingNavigation(business.id);

  return (
    <StepLayout
      activePane="schedule"
      title="How do you pay your team?"
      subtitle="This is what your pay periods get built from, so it is worth getting exactly right."
    >
      <Formik
        initialValues={initialValues}
        validate={validateScheduleStep}
        onSubmit={(values, helpers) => {
          helpers.setStatus(undefined);
          saveMutation.mutate(
            {
              step: "schedule",
              direction: "next",
              business: buildSchedulePayload(values),
            },
            {
              onSuccess: () => {
                helpers.setSubmitting(false);
              },
              onError: (error) => {
                helpers.setStatus(extractErrorMessage(error).message);
                helpers.setSubmitting(false);
              },
            },
          );
        }}
      >
        {({
          values,
          errors,
          status,
          submitCount,
          isSubmitting,
          setFieldValue,
          setStatus,
        }) => (
          <Form noValidate>
            {/* Leaving by the checklist saves the schedule the customer has chosen, so
                the task is marked finished either way. Nothing travels while the form is
                invalid, because the API would refuse it and strand them here. */}
            <PublishStepPayload
              payload={
                Object.keys(validateScheduleStep(values)).length
                  ? undefined
                  : { business: buildSchedulePayload(values) }
              }
            />
            <Stack className="w-full" gap={6} align="stretch">
              <Card className="w-full gap-0 py-0">
                <CardContent className="p-6 sm:p-8">
                  <Stack className="w-full" gap={6} align="stretch">
                    <AutoComplete
                      label="Pay schedule"
                      placeholder="Select a pay schedule"
                      required
                      selectedValue={values.cadence}
                      onSelectedValueChange={(value) => {
                        void setFieldValue("cadence", value);
                        void setFieldValue("startDayOfWeek", "");
                        void setFieldValue("periodStartReferenceDate", null);
                        setStartDaySearch("");
                      }}
                      searchValue={cadenceSearch}
                      onSearchValueChange={setCadenceSearch}
                      error={submitCount > 0 ? errors.cadence : undefined}
                      showErrorMessage
                      items={PAY_PERIOD_CADENCE_OPTIONS}
                      fullWidth
                    />

                    {needsStartDay(values.cadence) && (
                      <AutoComplete
                        label="First day of the pay period"
                        placeholder="Select a day"
                        required
                        selectedValue={values.startDayOfWeek}
                        onSelectedValueChange={(value) =>
                          setFieldValue("startDayOfWeek", value)
                        }
                        searchValue={startDaySearch}
                        onSearchValueChange={setStartDaySearch}
                        error={
                          submitCount > 0 ? errors.startDayOfWeek : undefined
                        }
                        showErrorMessage
                        items={START_DAY_OF_WEEK_OPTIONS}
                        fullWidth
                      />
                    )}

                    {needsReferenceDate(values.cadence) && (
                      <Stack className="w-full" align="start" gap={1}>
                        <TypeableDatePicker
                          name="periodStartReferenceDate"
                          label="Pay period start reference date"
                          value={values.periodStartReferenceDate}
                          onChange={(date) =>
                            setFieldValue("periodStartReferenceDate", date)
                          }
                          open={calendarOpen}
                          onOpenChange={setCalendarRequested}
                          error={
                            submitCount > 0
                              ? errors.periodStartReferenceDate
                              : undefined
                          }
                          fullWidth
                        />
                        <FieldError
                          message={
                            submitCount > 0
                              ? errors.periodStartReferenceDate
                              : undefined
                          }
                        />
                        <Text variant="caption" tone="muted">
                          Pick a date you know one of your two-week pay periods
                          started. It tells us which weeks are on.
                        </Text>
                      </Stack>
                    )}

                    {/* The banner is only for a save that failed: every validation
                          message sits on its own field. */}
                    {status && <Alert color="danger">{status}</Alert>}
                  </Stack>
                </CardContent>
              </Card>

              {/* Grouped bottom-right: two buttons pushed to opposite edges read as two
                  stranded controls, a pair reads as one decision. */}
              <Stack
                className="w-full"
                direction="row"
                gap={2}
                justify="end"
                align="center"
              >
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting || saveMutation.isPending}
                  onClick={() => {
                    setStatus(undefined);
                    saveMutation.mutate(
                      {
                        step: "schedule",
                        direction: "back",
                        // Back is leaving the step, so it carries what Continue would.
                        // Sending only the move meant valid edits survived while the
                        // component stayed mounted and were gone after a reload, which
                        // is not what "the server is the state" promises. Nothing
                        // travels while the form is invalid: the API validates what it
                        // is given, and a rejected Back would strand them here.
                        ...(Object.keys(validateScheduleStep(values)).length
                          ? {}
                          : { business: buildSchedulePayload(values) }),
                      },
                      {
                        onError: (error) =>
                          setStatus(extractErrorMessage(error).message),
                      },
                    );
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || saveMutation.isPending}
                  aria-busy={
                    isSubmitting || saveMutation.isPending || undefined
                  }
                >
                  {(isSubmitting || saveMutation.isPending) && (
                    <Spinner aria-hidden data-icon="inline-start" />
                  )}
                  Continue
                </Button>
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
    </StepLayout>
  );
};
